import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import AlwaysOnSummary from './AlwaysOnSummary'
import { useAppDispatch } from 'hooks/reduxHooks'
import OfferBNPLAd from '../OfferBNPLAd'
import GeoContext from 'contexts/geoContext'
import Breakpoint from 'components/Common/Breakpoint'
import LineEyeIcon from 'components/Luxkit/Icons/line/LineEyeIcon'
import { getOfferExtra, OFFER_VIEWS_24_HOURS_THRESHOLD } from 'selectors/offerExtraSelector'
import { fetchOfferExtraById } from 'actions/OfferActions'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import DepositsAvailableTag from 'components/Common/SecureWithDeposit/DepositsAvailableTag'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheapestAvailablePackageOption,
  getCheapestSelectedPackageOption,
  getTacticalPackageOptions,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import ContactUSP from '../ContactUSP'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import AlwaysOnPropertyDetails from './AlwaysOnPropertyDetails'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import moment from 'moment'

const OfferIntroWrapper = styled(LayoutContainer)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(30)} ${rem(12)};
  }
`

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content 1fr;
  }
`

const SummaryBlock = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    display: block;
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const Summary = styled(AlwaysOnSummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(370)};
  }
`

const OfferOtherActions = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-row: 1;
    grid-column: 2;
  }
`

const MobileBNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  order: 1;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const OfferViews = styled.div`
  display: flex;
  padding: ${rem(8)};
  justify-content: center;
  color: ${props => props.theme.palette.highlight.tertiary.normalForeground};
  background-color: ${props => props.theme.palette.highlight.tertiary.lightBackground};
`

const MobileOfferViews = styled(OfferViews)`
  position: relative;
  top: -${rem(20)};
  margin: 0 -${rem(20)};
`

const CenteredLabelGroup = styled(LabelGroup)`
  align-items: center;
`

interface Props {
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  promotions: Array<OfferPackageOptionView>;
  checkIn?: string;
  offer: App.HotelOffer;
  className?: string;
  onViewPackageOptions: () => void;
  offerExtra: App.OfferExtra;
  shouldShowSalesPhoneUSP: boolean;
  defaultDepositAmountPercentage: number;
  shareConfig: ShareConfig;
}

function AlwaysOnIntro(props: Props) {
  const {
    offer,
    promotions,
    className,
    onViewPackageOptions,
    offerExtra,
    cheapestOption,
    cheapestSelectedOption,
    checkIn,
    shouldShowSalesPhoneUSP,
    defaultDepositAmountPercentage,
    shareConfig,
  } = props

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { currentCurrency } = useContext(GeoContext)
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const packageView = cheapestSelectedOption ?? cheapestOption
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, packageView.hotelTotal, packageView.package)

  const urgencyToday = useMemo(() => {
    if (offerExtra && offerExtra.viewsInLast24Hours >= OFFER_VIEWS_24_HOURS_THRESHOLD) {
      return {
        captionText: `${offerExtra.viewsInLast24Hours} people viewed this offer today`,
        Icon: LineEyeIcon,
      }
    }
  }, [offerExtra])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  return (
    <OfferIntroWrapper>
      {urgencyToday && <Breakpoint max="mobile">
        <MobileOfferViews>
          <CaptionTextBlock variant="medium" weight="bold" startIcon={<urgencyToday.Icon />}>
            {urgencyToday.captionText}
          </CaptionTextBlock>
        </MobileOfferViews>
      </Breakpoint> }
      <OfferIntroGrid data-testid="offer-intro">
        <CenteredLabelGroup>
          {offer.isAgentHubExclusive && <PromotionLabel key="agenthub_exclusive" variant="engagement">Agent Exclusive</PromotionLabel>}
          {depositsAvailable && <DepositsAvailableTag offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>}
        </CenteredLabelGroup>
        <OfferOtherActions>
          <TripGuard inverse>
            <ShareModal shareConfig={shareConfig}>
              <ShareWithFriendsButton outdent="right" />
            </ShareModal>
          </TripGuard>
          <TripGuard>
            <ShareModal shareConfig={shareConfig}>
              <ShareWithFriendsButton />
            </ShareModal>
            <BookmarkButton offer={offer} />
          </TripGuard>
        </OfferOtherActions>
        <AlwaysOnPropertyDetails
          className={className}
          offer={offer}
          promotions={promotions}
          depositsAvailable={depositsAvailable}
        />
        <SummaryBlock>
          <div>
            {urgencyToday && <OfferViews>
              <CaptionTextBlock variant="medium" weight="bold" startIcon={<urgencyToday.Icon />}>
                {urgencyToday.captionText}
              </CaptionTextBlock>
            </OfferViews> }
            <Summary onViewPackageOptions={onViewPackageOptions} />
          </div>
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
        <MobileBNPLAd prices={allPrices} />
        {(shouldShowSalesPhoneUSP || promotions.length > 0) && <ContactUSP />}
      </OfferIntroGrid>
    </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  promotions: getTacticalPackageOptions(state, appState),
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice, 300),
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  checkIn: state.checkInDate,
  offerExtra: getOfferExtra(appState, state.offer?.id),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(AlwaysOnIntro)
